/**
 * EventAnchorForm — state when and where the meeting actually is (§ G3, § G21).
 *
 * This is the thing that makes every other verdict on the roster mean anything.
 * Without an anchor, `assessAnchor` returns `no_anchor` for every attendee and
 * the roster reads "No meeting time set" all the way down — honest, and useless.
 * The old code filled this gap by quietly using the organizer's own first flight
 * date, which is exactly the kind of confident wrong answer § G3 exists to stop.
 *
 * ── Why the END and the VENUE belong here too (§ G21) ───────────────────────
 * An organizer states one sentence: "it's in Alpharetta, 11 to 2". This form used
 * to accept a third of it, and the two omissions were not cosmetic:
 *   - no END → return legs had no floor, so a 1pm flight home out of a meeting
 *     that runs until 2pm looked perfectly fine;
 *   - no VENUE DISTANCE → `getRoster` passed `ground_minutes: null`, so EVERY
 *     attendee's verdict was `unknown`, on every row, forever.
 * Three inputs, one save, because they are one thought — and because a
 * half-stated meeting is what produced both of those silences.
 */
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Eye, EyeOff, MapPin } from 'lucide-react';
import { travelGroupApi, type AnchorRetime } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  zonedInputToIso,
  isoToZonedInput,
  browserTimezone,
  retimeNote,
  zoneAbbrev,
  zoneMismatchNote,
} from '@/pages/eventsPageView';
import { anchorHeadline } from './organizerRosterView';

export default function EventAnchorForm({ groupId }: { groupId: string }) {
  const qc = useQueryClient();
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [destination, setDestination] = useState('');
  const [venue, setVenue] = useState('');
  const [transit, setTransit] = useState('');
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [retimed, setRetimed] = useState<AnchorRetime | null>(null);

  const { data: roster } = useQuery({
    queryKey: ['travel-group-roster', groupId],
    queryFn: () => travelGroupApi.roster(groupId).catch(() => null),
    retry: false,
  });

  // Seed the form from the server once the roster arrives, without clobbering
  // edits already in progress.
  // ⚠️ § G24 — seeded and submitted in the MEETING CITY's zone, not the
  // browser's. The stored `anchor.timezone` is only used to WARN when it is not
  // the city's; reading the input back in it would keep a mis-zoned meeting
  // looking correct forever, which is exactly how this survived.
  const cityZone = roster?.event?.destination_timezone ?? null;
  const editZone = cityZone ?? browserTimezone();

  useEffect(() => {
    if (!roster) return;
    setStartsAt((v) => (v === '' ? isoToZonedInput(roster.anchor.start_at, editZone) : v));
    setEndsAt((v) => (v === '' ? isoToZonedInput(roster.event?.anchor_end_at, editZone) : v));
    setDestination((v) => (v === '' ? roster.event?.destination_airport ?? '' : v));
    setVenue((v) => (v === '' ? roster.event?.venue ?? '' : v));
    setTransit((v) =>
      v === ''
        ? roster.event?.venue_transit_minutes == null
          ? ''
          : String(roster.event.venue_transit_minutes)
        : v
    );
  }, [roster, editZone]);

  const mut = useMutation({
    mutationFn: () =>
      travelGroupApi.setAnchor(groupId, {
        anchor_start_at: zonedInputToIso(startsAt, editZone),
        // ⚠️ These three are WRITE-THROUGH on the server: sending null clears
        // them. That is deliberate (a mis-entered end time has to be removable)
        // and it is why this form always posts the whole § G21 set together
        // rather than only the field that was touched.
        anchor_end_at: zonedInputToIso(endsAt, editZone),
        venue: venue.trim() || null,
        // An empty box means "I don't know", never 0 minutes. `Number('')` is 0,
        // and a 0 here would tell every attendee the airport IS the venue.
        venue_transit_minutes: transit.trim() === '' ? null : Number(transit.trim()),
        // The zone the wall clock above was MEANT in — the city's whenever we
        // know it. Sending the browser's zone is what stored Wade's 11:00 ATL
        // meeting as 11:00 Central and shifted every deadline by an hour.
        anchor_timezone: editZone,
        destination_airport: destination.trim().toUpperCase() || null,
      }),
    onSuccess: (res) => {
      setError('');
      setOpen(false);
      setRetimed(res.retimed ?? null);
      qc.invalidateQueries({ queryKey: ['travel-group-roster', groupId] });
    },
    onError: (e: any) => setError(e?.response?.data?.error || e.message),
  });

  const faresMut = useMutation({
    mutationFn: (next: boolean) => travelGroupApi.setAnchor(groupId, { organizer_sees_fares: next }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['travel-group-roster', groupId] }),
  });

  if (!roster) return null;
  const anchorSet = !!roster.anchor.start_at;
  const event = roster.event;
  const shopWindow = event?.window;
  // § G24 — say which clock these times are on. A bare "Starts" is what let an
  // organizer in Dallas type 11:00 for an Atlanta meeting and never see the hour
  // they lost.
  const zoneLabel = zoneAbbrev(editZone, roster.anchor.start_at) || (editZone ?? '');
  const mismatch = zoneMismatchNote(roster.anchor.timezone, cityZone, event?.destination_airport);
  const note = retimeNote(
    retimed ?? { updated: 0, left_alone: 0, window: { latest_arrival: null, earliest_departure: null } }
  );

  return (
    <section className="rounded-lg border border-border bg-background p-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="flex items-center gap-1.5 text-sm font-medium">
          <CalendarClock className="h-4 w-4" />
          Meeting time
        </span>

        {/*
          Show the time. A panel titled "Meeting time" that renders only a
          "Change" button — which is what this was until it was driven in a
          browser — omits the single fact it exists to carry, and forces the
          organizer to hunt for it further down the roster header.
        */}
        {anchorSet && (
          <span className="text-sm text-foreground">
            {anchorHeadline({
              start_at: roster.anchor.start_at,
              timezone: roster.anchor.timezone,
              label: roster.anchor.label,
              end_at: event?.anchor_end_at ?? null,
            })}
            {event?.destination_airport && (
              <span className="ml-1.5 text-xs text-muted-foreground">
                in {event.destination_airport}
              </span>
            )}
          </span>
        )}

        {!anchorSet && (
          // Stated plainly, because until this is set the whole roster is blind.
          <span className="text-xs text-destructive">
            Not set — nobody's arrival can be checked against it yet.
          </span>
        )}

        {!open && (
          <button
            onClick={() => setOpen(true)}
            className="rounded-full border border-dashed border-border px-2.5 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-muted"
          >
            {anchorSet ? 'Change' : 'Set meeting time'}
          </button>
        )}

        {/* § G6 — fare visibility is a decision, and it says which mode it is in. */}
        <button
          onClick={() => faresMut.mutate(!roster.fares_visible)}
          disabled={faresMut.isPending}
          className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          {roster.fares_visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
          {roster.fares_visible ? 'Fares visible to you' : 'Fares hidden'}
        </button>
      </div>

      {/* § G24 — a meeting stored in the organizer's laptop zone is not silently
          rewritten (that would move a real meeting by an hour unasked); it is
          stated, with the one-save fix. */}
      {mismatch && <p className="mt-1.5 text-xs text-amber-700">{mismatch}</p>}

      {/* § G21 — the place, and what it does to the shopping. Both stated, so the
          consequence of the number typed here is visible without hunting. */}
      {!open && (event?.venue || shopWindow?.note) && (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          {event?.venue && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
              {event.venue}
              {event.venue_transit_minutes != null && (
                <span className="text-muted-foreground/70">
                  · {event.venue_transit_minutes} min from the airport
                </span>
              )}
            </span>
          )}
          {shopWindow?.note && (
            <span className={shopWindow.latest_arrival ? '' : 'text-amber-700'}>{shopWindow.note}</span>
          )}
        </div>
      )}

      {/* What the last save did to other people's legs. An organizer must never
          have to infer that changing the meeting moved eight itineraries. */}
      {!open && note && <p className="mt-1.5 text-xs text-foreground">{note}</p>}

      {open && (
        <form
          className="mt-2 flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            mut.mutate();
          }}
        >
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">
              Starts{zoneLabel && <span className="text-muted-foreground/70"> ({zoneLabel})</span>}
            </span>
            <Input
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              className="h-8 w-52 text-sm"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">
              Ends{zoneLabel && <span className="text-muted-foreground/70"> ({zoneLabel})</span>}
            </span>
            <Input
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              title="When the meeting finishes — nobody gets shopped onto a flight that leaves before this"
              className="h-8 w-52 text-sm"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">City airport</span>
            <Input
              placeholder="ATL"
              value={destination}
              onChange={(e) => setDestination(e.target.value.toUpperCase())}
              className="h-8 w-24 font-mono text-sm"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Where</span>
            <Input
              placeholder="Alpharetta, GA"
              value={venue}
              onChange={(e) => setVenue(e.target.value)}
              title="The venue, in your own words"
              className="h-8 w-44 text-sm"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Airport → venue</span>
            <div className="flex items-center gap-1">
              <Input
                type="number"
                min={0}
                max={600}
                inputMode="numeric"
                placeholder="50"
                value={transit}
                onChange={(e) => setTransit(e.target.value)}
                title="Deplaning, bags, the drive and the buffer you want at the door — one number, in minutes"
                className="h-8 w-20 text-sm"
              />
              <span className="text-xs text-muted-foreground">min</span>
            </div>
          </label>
          <Button type="submit" size="sm" className="h-8" disabled={mut.isPending}>
            {mut.isPending ? 'Saving…' : 'Save'}
          </Button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            cancel
          </button>
          {error && <span className="text-xs text-destructive">{error}</span>}
          {/* Says what the allowance is FOR, because leaving it blank is a real
              choice that costs every attendee a verdict. */}
          <p className="w-full text-xs text-muted-foreground/80">
            The allowance is how the working-backwards is done: land by (start − allowance), fly home
            from (end + allowance). Leave it blank and no arrival deadline is claimed — every verdict
            stays &ldquo;unknown&rdquo; rather than guessed.
          </p>
        </form>
      )}
    </section>
  );
}
